// Deterministic compiler: validated Creative Document → Delivery Artifact v1
// (RUNTIME-CONTRACT-V1.md §2). NOT the final HTML/JS: the immutable,
// runtime-consumable representation.
//
// Determinism: the artifact is a pure function of (document, policy,
// runtimeMajor, COMPILER_VERSION). No clock, no randomness, no ids are
// generated; order-insensitive collections are sorted; defaults are made
// explicit; bytes are RFC 8785 canonical JSON; the hash is SHA-256 of them.
//
// Excluded from the artifact (never served): authoring sidecar (never an
// input), layer names, preset provenance, intendedProduct, internal titles,
// link labels, research objective, internal asset/revision ids, any tenant
// identifier. Billable product classification is NOT computed here.
//
// Format variants: each layout carries its RESOLVED presentation (base
// element values with that variant's overrides applied, onlyIn and
// childrenByLayout resolved). Nothing is derived by proportional scaling.

import { EVENT_TAXONOMY_VERSION } from "@interactads/domain";
import { FAIL } from "./schema/dsl.js";
import {
  CREATIVE_DOCUMENT_SCHEMA_VERSION,
  type AnimationTrack,
  type AssetRef,
  type CreativeDocument,
  type Element,
  type JsonValue,
  type Layout,
  type LayoutOverride,
} from "./schema/document.js";
import { IssueSink, sortIssues, type ValidationIssue } from "./issues.js";
import { canonicalJson, sha256Hex } from "./canonical.js";
import { capabilityProfile, initialAssetSet, type CapabilityProfile } from "./analysis.js";
import { combinePolicies, UNRESTRICTED_POLICY, type ResourcePolicy } from "./policy.js";
import { resolveComponent } from "./registry.js";
import { buildIndex, inLayout, type DocIndex } from "./validate/context.js";
import { validateCreativeDocument } from "./validate/index.js";

export const DELIVERY_ARTIFACT_FORMAT = "interactads.delivery-artifact" as const;
export const DELIVERY_ARTIFACT_FORMAT_VERSION = 1 as const;
/** Bump on ANY change to compiler output; golden fixture hashes pin it. */
export const COMPILER_VERSION = "1.1.0";
export const SUPPORTED_RUNTIME_MAJORS: readonly number[] = [1];
/** Minimum runtime (within major 1) required by every feature of schemaVersion 2. */
export const MIN_RUNTIME_V1 = "1.0.0";
/** Minimum runtime that serves an AI-generated HTML5 project in its isolated frame (ADR-0043). */
export const MIN_RUNTIME_HTML5 = "1.2.0";

type Json = JsonValue;
type JsonRecord = { [key: string]: Json };

export interface ArtifactLayoutV1 {
  readonly id: string;
  readonly formatKeys: readonly string[];
  readonly sizing: Layout["sizing"];
  readonly fallback: { readonly asset: string };
  /** Children of each scene in this variant (onlyIn and childrenByLayout applied). */
  readonly scenes: Readonly<Record<string, readonly string[]>>;
  readonly overlay: { readonly below: readonly string[]; readonly above: readonly string[] };
  /** Resolved presentation per element present in this variant. */
  readonly elements: Readonly<Record<string, JsonRecord>>;
}

export interface ArtifactEventSchemaV1 {
  readonly taxonomyVersion: typeof EVENT_TAXONOMY_VERSION;
  readonly scenes: readonly string[];
  readonly elements: readonly string[];
  readonly links: readonly string[];
  /** Question id → option ids (empty for scale questions). */
  readonly questions: Readonly<Record<string, readonly string[]>>;
  /** Form id → field id → option ids (empty for rating/local text fields). */
  readonly forms: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>>;
  /** Field ids whose values may appear in events (choice fields only; local text never). */
  readonly collectableFields: Readonly<Record<string, readonly string[]>>;
  readonly custom: Readonly<Record<string, { readonly props: JsonRecord; readonly maxPerInstance: number }>>;
}

/**
 * AI-generated HTML5 project served in an isolated frame (ADR-0043). Only data: every file is
 * pinned by SHA-256 and served by the delivery plane after re-verification; nothing is executed
 * outside the sandboxed frame. `ctaLinkId` is the document link the ad's CTA opens (macros and
 * click tracking applied by the runtime, never a URL chosen by the generated code).
 */
export interface ArtifactHtml5V1 {
  readonly entryPoint: "index.html";
  readonly layoutId: string;
  readonly width: number;
  readonly height: number;
  readonly manifestSha256: string;
  readonly ctaLinkId: string | null;
  readonly files: ReadonlyArray<{ readonly path: string; readonly sha256: string; readonly bytes: number }>;
}

export interface DeliveryArtifactV1 {
  readonly format: typeof DELIVERY_ARTIFACT_FORMAT;
  readonly formatVersion: typeof DELIVERY_ARTIFACT_FORMAT_VERSION;
  readonly compilerVersion: string;
  readonly requires: {
    readonly schemaVersion: typeof CREATIVE_DOCUMENT_SCHEMA_VERSION;
    readonly runtimeMajor: number;
    readonly minRuntime: string;
    readonly features: readonly string[];
  };
  readonly policy: ResourcePolicy;
  readonly meta: { readonly locale: string; readonly accessibility: JsonRecord };
  readonly initialSceneId: string;
  readonly scenes: readonly JsonRecord[];
  readonly elements: readonly JsonRecord[];
  readonly layouts: readonly ArtifactLayoutV1[];
  readonly assets: Readonly<Record<string, JsonRecord>>;
  readonly fonts: readonly JsonRecord[];
  readonly links: readonly JsonRecord[];
  readonly variables: readonly JsonRecord[];
  readonly timers: readonly JsonRecord[];
  readonly rules: readonly JsonRecord[];
  readonly events: readonly JsonRecord[];
  readonly survey?: JsonRecord;
  readonly forms: readonly JsonRecord[];
  /** Declared game module (7.6F), only when the document has one (existing artifacts unchanged). */
  readonly game?: JsonRecord;
  /** AI-generated HTML5 project (ADR-0043), only when the document has one. */
  readonly html5?: ArtifactHtml5V1;
  readonly fallback: { readonly linkId: string; readonly alt: string };
  readonly initialSet: Readonly<Record<string, readonly string[]>>;
  readonly eventSchema: ArtifactEventSchemaV1;
}

const byKey =
  <T>(key: (t: T) => string) =>
  (a: T, b: T) =>
    key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0;

const strip = (value: object, keys: readonly string[]): JsonRecord =>
  Object.fromEntries(
    Object.entries(value as Readonly<Record<string, Json | undefined>>).filter(
      (entry): entry is [string, Json] => !keys.includes(entry[0]) && entry[1] !== undefined
    )
  );

/** Game module without authoring labels (game and runner segment names); existing games compile unchanged. */
function compileGame(game: NonNullable<CreativeDocument["game"]>): JsonRecord {
  const out = strip(game, ["name"]);
  if (game.runner)
    out.runner = strip({ ...game.runner, segments: game.runner.segments.map((sg) => strip(sg, ["name"])) }, []);
  return out;
}

const EXTENSIONS: Readonly<Record<string, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/avif": "avif",
  "image/gif": "gif",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "audio/mpeg": "mp3",
  "audio/mp4": "m4a",
  "audio/aac": "aac",
  "audio/webm": "weba",
  "audio/ogg": "ogg",
  "font/woff2": "woff2",
  "text/vtt": "vtt",
};

/** Content-addressed relative path; the loader prefixes the asset origin (RC-O7 OPEN). */
export function assetPath(asset: Pick<AssetRef, "sha256" | "mime">): string {
  return `assets/${asset.sha256}.${EXTENSIONS[asset.mime] ?? "bin"}`;
}

function normalizeTrack(t: AnimationTrack): JsonRecord {
  return strip(
    {
      ...t,
      delay: t.delay ?? 0,
      iterations: t.iterations ?? 1,
      direction: t.direction ?? "normal",
    },
    ["preset"]
  );
}

/** Element keys that a layout variant may override (moved to per-variant presentation). */
const PRESENTATION_KEYS = ["frame", "constraints", "visible", "typography", "style", "animations", "states"] as const;

function presentation(el: Element, override: LayoutOverride | undefined): JsonRecord {
  const textual = el.type === "text" || el.type === "button";
  const media = el.type === "image" || el.type === "video" ? el : undefined;
  const baseText = el.type === "text" ? el.content : el.type === "button" ? el.label : undefined;
  const typography = textual ? { ...el.typography, ...(override?.typography ?? {}) } : undefined;
  const style = el.style || override?.style ? { ...(el.style ?? {}), ...(override?.style ?? {}) } : undefined;
  const states = el.states || override?.states ? { ...(el.states ?? {}), ...(override?.states ?? {}) } : undefined;
  const animations = override?.animations ?? el.animations;
  return strip(
    {
      frame: override?.frame ?? el.frame,
      constraints: override?.constraints ?? el.constraints,
      visible: override?.visible ?? el.visible ?? true,
      typography,
      fit: media ? (override?.fit ?? media.fit) : undefined,
      style,
      text: textual ? (override?.content ?? baseText) : undefined,
      animations: animations?.map(normalizeTrack),
      states,
    },
    []
  );
}

function baseElement(el: Element): JsonRecord {
  const drop: string[] = ["name", "onlyIn", ...PRESENTATION_KEYS];
  if (el.type === "text") drop.push("content");
  if (el.type === "button") drop.push("label");
  if (el.type === "image" || el.type === "video") drop.push("fit");
  const out = strip({ ...el, opacity: el.opacity ?? 1 }, drop);
  if (el.template !== true) delete out.template;
  if (el.type === "component") {
    const r = resolveComponent(el.component);
    // Registry-parsed props: only declared keys survive (validated upstream).
    if (r.ok) {
      const props = r.definition.props.parse(el.props, "$", new IssueSink("compile"));
      if (props !== FAIL) out.props = props as Json;
    }
  }
  return out;
}

function compileLayout(index: DocIndex, layout: Layout): ArtifactLayoutV1 {
  const doc = index.doc;
  const present = (id: string) => inLayout(index, id, layout.id);
  const scenes: Record<string, string[]> = {};
  for (const scene of doc.scenes) {
    const order = scene.childrenByLayout?.[layout.id] ?? scene.children;
    scenes[scene.id] = order.filter(present);
  }
  const elements: Record<string, JsonRecord> = {};
  for (const el of doc.elements) if (present(el.id)) elements[el.id] = presentation(el, layout.overrides?.[el.id]);
  const fallback = doc.fallback?.perLayout[layout.id];
  return {
    id: layout.id,
    formatKeys: [...layout.formatKeys].sort(),
    sizing: layout.sizing,
    fallback: { asset: fallback?.asset ?? "" },
    scenes,
    overlay: { below: (doc.overlay?.below ?? []).filter(present), above: (doc.overlay?.above ?? []).filter(present) },
    elements,
  };
}

/**
 * Resolved presentation of ONE format variant: exactly the resolution the Delivery Artifact
 * carries (overrides, onlyIn and childrenByLayout applied; nothing scaled). Used by the editor
 * canvas and the preview so that what is edited is what is compiled. Input: a document that
 * passed at least the structure + reference layers (`canSave`).
 */
export function resolveVariant(doc: CreativeDocument, layoutId: string): ArtifactLayoutV1 | undefined {
  const index = buildIndex(doc);
  const layout = index.layouts.get(layoutId)?.value;
  return layout ? compileLayout(index, layout) : undefined;
}

function eventSchema(doc: CreativeDocument): ArtifactEventSchemaV1 {
  const sorted = (xs: Iterable<string>) => [...xs].sort();
  return {
    taxonomyVersion: EVENT_TAXONOMY_VERSION,
    scenes: sorted(doc.scenes.map((s) => s.id)),
    elements: sorted(doc.elements.map((e) => e.id)),
    links: sorted(doc.links.map((l) => l.id)),
    questions: Object.fromEntries(
      (doc.survey?.questions ?? []).map((q) => [q.id, q.kind === "scale" ? [] : sorted(q.options.map((o) => o.id))])
    ),
    forms: Object.fromEntries(
      (doc.forms ?? []).map((f) => [
        f.id,
        Object.fromEntries(f.fields.map((fd) => [fd.id, "options" in fd ? sorted(fd.options.map((o) => o.id)) : []])),
      ])
    ),
    collectableFields: Object.fromEntries(
      (doc.forms ?? []).map((f) => [
        f.id,
        sorted(f.fields.filter((fd) => fd.dataClass === "choice").map((fd) => fd.id)),
      ])
    ),
    custom: Object.fromEntries(
      (doc.events ?? []).map((e) => [e.name, { props: e.props ?? {}, maxPerInstance: e.maxPerInstance }])
    ),
  };
}

export interface BuildOptions {
  readonly runtimeMajor: number;
  readonly policy?: ResourcePolicy;
}

function compileHtml5(doc: CreativeDocument): ArtifactHtml5V1 | undefined {
  const p = doc.html5;
  if (!p) return undefined;
  // Only a project validated in the isolated browser preview is publishable (validator: HTML5_EXECUTION_NOT_APPROVED).
  if (p.validation !== "isolated-preview") throw new Error("HTML5_EXECUTION_NOT_APPROVED");
  const landing = doc.links.find((l) => l.kind === "landing") ?? doc.links[0];
  return {
    entryPoint: p.entryPoint,
    layoutId: p.layoutId,
    width: p.width,
    height: p.height,
    manifestSha256: p.manifestSha256,
    ctaLinkId: landing?.id ?? null,
    files: [...p.files].sort(byKey((f) => f.path)).map((f) => ({ path: f.path, sha256: f.sha256, bytes: f.bytes })),
  };
}

/** Builds the artifact from an ALREADY VALIDATED document (use compileCreative for untrusted input). */
export function buildDeliveryArtifact(doc: CreativeDocument, options: BuildOptions): DeliveryArtifactV1 {
  const html5 = compileHtml5(doc);
  const index = buildIndex(doc);
  const policy = options.policy ?? UNRESTRICTED_POLICY;
  const survey = doc.survey;
  return {
    format: DELIVERY_ARTIFACT_FORMAT,
    formatVersion: DELIVERY_ARTIFACT_FORMAT_VERSION,
    compilerVersion: COMPILER_VERSION,
    requires: {
      schemaVersion: CREATIVE_DOCUMENT_SCHEMA_VERSION,
      runtimeMajor: options.runtimeMajor,
      minRuntime: html5 ? MIN_RUNTIME_HTML5 : MIN_RUNTIME_V1,
      features: capabilityProfile(doc, index).features,
    },
    policy: { id: policy.id, version: policy.version, limits: { ...policy.limits } },
    meta: { locale: doc.meta.locale, accessibility: strip(doc.meta.accessibility, []) },
    initialSceneId: doc.initialSceneId,
    scenes: doc.scenes.map((s) =>
      strip({ ...s, onTimelineEnd: s.onTimelineEnd ?? "hold" }, ["name", "children", "childrenByLayout"])
    ),
    elements: [...doc.elements].sort(byKey((e) => e.id)).map(baseElement),
    // An HTML5 project exists in ONE size: only its layout is offered (never another format scaled).
    layouts: doc.layouts.filter((l) => !html5 || l.id === html5.layoutId).map((l) => compileLayout(index, l)),
    assets: Object.fromEntries(
      doc.assets.map((a) => [a.key, strip({ ...a, path: assetPath(a) }, ["key", "assetId", "revisionId", "role"])])
    ),
    fonts: [...(doc.fonts ?? [])]
      .sort(byKey((f) => f.key))
      .map((f) => strip({ ...f, weights: [...f.weights].sort((a, b) => a - b) }, [])),
    links: [...doc.links].sort(byKey((l) => l.id)).map((l) => strip(l, ["label", "kind"])),
    variables: [...(doc.variables ?? [])].sort(byKey((v) => v.id)).map((v) => strip(v, [])),
    timers: [...(doc.timers ?? [])].sort(byKey((t) => t.id)).map((t) =>
      strip(
        {
          ...t,
          repeat: t.repeat ?? false,
          autoStart: t.autoStart ?? false,
          pauseWhenHidden: t.pauseWhenHidden ?? true,
        },
        []
      )
    ),
    rules: (doc.rules ?? []).map((r) =>
      strip({ ...r, once: r.once ?? false, cooldownMs: r.cooldownMs ?? 0, enabled: r.enabled ?? true }, [])
    ),
    events: [...(doc.events ?? [])].sort(byKey((e) => e.name)).map((e) => strip(e, ["label"])),
    ...(survey
      ? {
          survey: strip({ ...survey, questions: [...survey.questions].sort(byKey((q) => q.id)) }, [
            "researchObjective",
            "sampleType",
          ]),
        }
      : {}),
    forms: [...(doc.forms ?? [])].sort(byKey((f) => f.id)).map((f) => strip(f, [])),
    ...(doc.game ? { game: compileGame(doc.game) } : {}),
    ...(html5 ? { html5 } : {}),
    // The fallback is optional (ADR-0042): empty link/alt when the document has none.
    fallback: { linkId: doc.fallback?.linkId ?? "", alt: doc.fallback?.alt ?? "" },
    initialSet: initialAssetSet(index),
    eventSchema: eventSchema(doc),
  };
}

export interface CompileOptions {
  readonly runtimeMajor: number;
  /** One or more policies; the effective policy is their intersection. */
  readonly policies?: readonly ResourcePolicy[];
}

export type CompileResult =
  | {
      readonly ok: true;
      readonly artifact: DeliveryArtifactV1;
      /** RFC 8785 canonical bytes (as text) of the artifact — what is stored and hashed. */
      readonly canonical: string;
      /** SHA-256 hex of `canonical`. */
      readonly artifactHash: string;
      readonly capabilityProfile: CapabilityProfile;
      /** Non-blocking validator findings. */
      readonly warnings: readonly ValidationIssue[];
    }
  | { readonly ok: false; readonly issues: readonly ValidationIssue[] };

/** Validates (all layers + policy) and compiles. Invalid input never produces an artifact. */
export async function compileCreative(input: unknown, options: CompileOptions): Promise<CompileResult> {
  if (!SUPPORTED_RUNTIME_MAJORS.includes(options.runtimeMajor)) {
    const sink = new IssueSink("compile");
    sink.add("UNSUPPORTED_RUNTIME_MAJOR", "$", "This compiler does not target the requested runtime major.", {
      requested: Number.isFinite(options.runtimeMajor) ? options.runtimeMajor : -1,
      supported: SUPPORTED_RUNTIME_MAJORS.join("|"),
    });
    return { ok: false, issues: sortIssues(sink.issues) };
  }
  const policy = combinePolicies(...(options.policies ?? [UNRESTRICTED_POLICY]));
  const result = validateCreativeDocument(input, { policy });
  if (!result.ok || !result.document) return { ok: false, issues: result.issues };
  const artifact = buildDeliveryArtifact(result.document, { runtimeMajor: options.runtimeMajor, policy });
  const canonical = canonicalJson(artifact);
  return {
    ok: true,
    artifact,
    canonical,
    artifactHash: await sha256Hex(canonical),
    capabilityProfile: capabilityProfile(result.document),
    warnings: result.issues.filter((i) => i.severity === "warning"),
  };
}

/** Variant selection for a Distribution format (exact key match; never scaled from another variant). */
export function selectLayoutForFormat<T extends { readonly id: string; readonly formatKeys: readonly string[] }>(
  layouts: readonly T[],
  format: string
): T | undefined {
  return layouts.find((l) => l.formatKeys.includes(format));
}
